/**
 * The book's index icons (24 × 24 line drawings, currentColor, the same pen as src/ui/icons.ts CONTROL_ICONS):
 * one per section tile on the book's home page and one per online panel. Original drawings, period things —
 * a clipboard, an envelope, a banknote, a fedora, a lamp-post, a holdall.
 */
import { CONTROL_ICONS } from '../ui/icons';

const line = (inner: string): string =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;

export type BookIcon =
  | 'jobs'
  | 'messages'
  | 'contacts'
  | 'map'
  | 'money'
  | 'motor'
  | 'help'
  | 'settings'
  | 'online'
  | 'street'
  | 'gang'
  | 'crew'
  | 'pockets'
  | 'chat'
  | 'freight'
  | 'firm'
  | 'premises'
  | 'events'
  | 'emote'
  | 'warn'
  | 'record'
  | 'week'
  | 'trade'
  | 'hideout'
  | 'profile';

export const BOOK_ICONS: Record<BookIcon, string> = {
  // a clipboard with its clip and three lines of work
  jobs: line('<rect x="5" y="4" width="14" height="17" rx="1.6"/><path d="M9 4V2.6h6V4"/><path d="M8.5 9.5h7M8.5 13h7M8.5 16.5h4"/>'),
  // a letter in its envelope
  messages: line('<rect x="3" y="5.5" width="18" height="13" rx="1.5"/><path d="M3.6 6.5l8.4 6.6 8.4-6.6"/>'),
  // a head and shoulders, the way an address book draws a person
  contacts: line('<circle cx="12" cy="8.6" r="3.4"/><path d="M5 20.5c.8-4 3.6-6.2 7-6.2s6.2 2.2 7 6.2"/>'),
  map: CONTROL_ICONS.map,
  // a banknote: border, the portrait oval, the corner figures
  money: line('<rect x="2.5" y="6" width="19" height="12" rx="1.4"/><ellipse cx="12" cy="12" rx="2.8" ry="3.3"/><path d="M5.5 9v1.5M18.5 13.5V15"/>'),
  motor: CONTROL_ICONS.car,
  // a question mark in a ring
  help: line('<circle cx="12" cy="12" r="9"/><path d="M9.4 9.4a2.7 2.7 0 1 1 3.9 2.4c-.9.4-1.3 1.1-1.3 2v.6"/><circle cx="12" cy="17.3" r=".7" fill="currentColor"/>'),
  // a wireless set's tuning knob: the knob, its pointer and the dial ticks
  settings: line('<circle cx="12" cy="12" r="5.2"/><path d="M12 12l2.6-2.6"/><path d="M12 2.8v2M12 19.2v2M2.8 12h2M19.2 12h2M5.5 5.5l1.4 1.4M17.1 17.1l1.4 1.4M5.5 18.5l1.4-1.4M17.1 6.9l1.4-1.4"/>'),
  // a radio mast sending: the shared city
  online: line('<path d="M12 10.5V21M8.5 21 12 10.5 15.5 21M9.6 17.5h4.8"/><circle cx="12" cy="8.5" r="1.3" fill="currentColor"/><path d="M8.2 5.2a5.3 5.3 0 0 0 0 6.6M15.8 5.2a5.3 5.3 0 0 1 0 6.6M5.6 3a8.8 8.8 0 0 0 0 11M18.4 3a8.8 8.8 0 0 1 0 11"/>'),
  // a gas lamp-post on the kerb
  street: line('<path d="M12 21V9M8.5 21h7"/><path d="M9 4.5h6l-1 4.5h-4Z" fill="currentColor" fill-opacity=".22"/><path d="M12 2.5v2M9.8 13h4.4"/>'),
  // a fedora with its band
  gang: line('<path d="M2.8 15.6c3.3 1.8 15.1 1.8 18.4 0"/><path d="M6.4 15.4c0-4.6 1.1-8.4 2.8-8.4 1 0 1.7 1.1 2.8 1.1s1.8-1.1 2.8-1.1c1.7 0 2.8 3.8 2.8 8.4"/><path d="M6.6 12.6h10.8" stroke-width="2.6"/>'),
  // two of you
  crew: line('<circle cx="9" cy="8.4" r="2.8"/><circle cx="16.8" cy="9.4" r="2.3"/><path d="M3.4 19.8c.6-3.4 2.9-5.4 5.6-5.4s5 2 5.6 5.4"/><path d="M14.8 14.5c.6-.3 1.3-.4 2-.4 2.2 0 3.9 1.6 4.4 4.4"/>'),
  // a holdall: what you carry
  pockets: line('<rect x="3.5" y="9" width="17" height="11" rx="2.2"/><path d="M8.5 9V6.6A1.6 1.6 0 0 1 10.1 5h3.8a1.6 1.6 0 0 1 1.6 1.6V9"/><path d="M3.5 13.5h17M11 13.5v2h2v-2"/>'),
  // a speech bubble
  chat: line('<path d="M4 5h16v10.5H10.5l-4.5 3.8v-3.8H4Z"/><path d="M8 9h8M8 12h5"/>'),
  // a slatted crate
  freight: line('<rect x="3.5" y="6" width="17" height="13" rx="1"/><path d="M3.5 10.3h17M3.5 14.7h17M7.5 6v13M16.5 6v13"/>'),
  // a money bag tied at the neck: the firm's jobs
  firm: line('<path d="M9.2 7.5h5.6l-1.2-2.8h-3.2Z"/><path d="M9.2 7.5C5.4 9.8 4 13.2 4.6 16.4 5.2 19.4 8 20.5 12 20.5s6.8-1.1 7.4-4.1c.6-3.2-.8-6.6-4.6-8.9"/><path d="M13.6 11.6c-.4-.6-1-.9-1.8-.9-1 0-1.7.6-1.7 1.4 0 1.9 3.8 1.2 3.8 3.2 0 .8-.8 1.5-1.9 1.5-.9 0-1.6-.4-2-1M11.9 9.8v1M11.9 16.8v1"/>'),
  // a warehouse with a roller door
  premises: line('<path d="M3 20.5v-11l9-5 9 5v11M2 20.5h20"/><path d="M8.5 20.5v-7h7v7M8.5 16h7"/>'),
  // a folded evening paper
  events: line('<path d="M4 5h12.5v13.5A2.5 2.5 0 0 0 19 21H6.5A2.5 2.5 0 0 1 4 18.5Z"/><path d="M16.5 9H20v9.5a2.5 2.5 0 0 1-2.5 2.5"/><path d="M7 8.5h6.5M7 12h6.5M7 15.5h4"/>'),
  emote: CONTROL_ICONS.use,
  warn: CONTROL_ICONS.warn,
  // a prize rosette with its two tails: your record
  record: line('<circle cx="12" cy="9" r="5.6"/><circle cx="12" cy="9" r="2.4"/><path d="M8.7 13.6 7.2 21l4.8-2.5 4.8 2.5-1.5-7.4"/>'),
  // a two-handled cup on its plinth: the week's honours
  week: line('<path d="M7.5 3.5h9V8a4.5 4.5 0 0 1-9 0Z"/><path d="M7.5 5.5H4.8a2.7 2.7 0 0 0 3 3.7M16.5 5.5h2.7a2.7 2.7 0 0 1-3 3.7"/><path d="M12 12.5v3.5M8 20.5h8M9.4 20.5l.7-4.5h3.8l.7 4.5"/>'),
  // a front door under its fanlight, the knocker and the step: the gang's hideout
  hideout: line('<path d="M6.5 21V9.5a5.5 5.5 0 0 1 11 0V21"/><path d="M6.5 9.5h11M12 4v5.5"/><circle cx="14.6" cy="15" r=".9"/><path d="M4 21h16"/>'),
  // a man in a flat cap beside a motor's roofline: the profile page, you and your car
  profile: line('<circle cx="8" cy="7.2" r="2.6"/><path d="M5 7h6"/><path d="M3.2 19.5c.5-3.6 2.4-5.6 4.8-5.6s4.3 2 4.8 5.6"/><path d="M13 19.5v-3.2l1.6-3h4.2l1.8 3v3.2"/><path d="M13 16.3h8.6"/><circle cx="15" cy="19.5" r="1.1"/><circle cx="19.6" cy="19.5" r="1.1"/>'),
  // a dealer's hand scales: Wilf's book, buying and selling
  trade: line('<path d="M12 3.5V20M8 20.5h8M4.5 7h15"/><circle cx="12" cy="4.6" r="1.1" fill="currentColor"/><path d="M4.5 7 2.3 13h4.4Z"/><path d="M19.5 7l-2.2 6h4.4Z"/><path d="M2.3 13a2.2 1.4 0 0 0 4.4 0M17.3 13a2.2 1.4 0 0 0 4.4 0"/>'),
};
